import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Music, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useRadio } from "@/context/RadioContext";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import ListenFull from "@/components/ListenFull";

const MUSIC_KEY = "atlas.music";
const KIND_KEY = "atlas.songKind";
const src = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function KindSwitch({ kind, onChange }) {
  const { t } = useLang();
  return (
    <span className="inline-flex rounded-full bg-sand/10 p-0.5" data-testid="song-kind-switch">
      {["classic", "modern"].map((k) => (
        <button key={k} onClick={() => onChange(k)} aria-pressed={kind === k}
          className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider transition-colors ${kind === k ? "bg-ochre text-white" : "text-sand/70 hover:text-sand"}`}
          data-testid={`song-kind-${k}`}>
          {t(`song_${k}`)}
        </button>
      ))}
    </span>
  );
}

function MusicToggle({ enabled, onToggle }) {
  const { t } = useLang();
  const label = enabled ? t("music_off") : t("music_on");
  return (
    <button onClick={onToggle} aria-label={label} aria-pressed={enabled} title={label} data-testid="music-enabled-toggle"
      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${enabled ? "bg-ochre text-white" : "bg-sand/15 text-sand/70 hover:bg-sand/25"}`}>
      {enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
    </button>
  );
}

function ArtworkButton({ song, playing, onClick }) {
  const { t } = useLang();
  return (
    <button onClick={onClick} aria-label={playing ? t("song_pause") : t("song_play")} data-testid="song-toggle"
      className="relative w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-sandDeep/30 group">
      {song.artwork ? <img src={song.artwork} alt="" className="w-full h-full object-cover" /> : <Music className="w-5 h-5 m-auto" />}
      <span className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/55 transition-colors">
        {playing ? <Pause className="w-5 h-5 text-white" fill="currentColor" /> : <Play className="w-5 h-5 text-white ms-0.5" fill="currentColor" />}
      </span>
    </button>
  );
}

/** Owns the <audio> element: autoplay on country open, pause/resume, radio exclusivity. */
function useSongAudio(song, code, enabled) {
  const radio = useRadio();
  const audioRef = useRef(null);
  const radioRef = useRef(radio);
  radioRef.current = radio;
  const radioPlaying = radio?.playing;
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  // Autoplay (allowed after the user's click); deep links just show Play. Skipped while the radio is playing.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    setProgress(0);
    el.load();
    if (!enabled || radioRef.current?.playing) { setPlaying(false); return; }
    el.play().then(() => { setPlaying(true); radioRef.current?.pause(); trackEvent("song_autoplay", { country_code: code }); })
      .catch(() => setPlaying(false));
    return () => { el.pause(); };
  }, [song?.preview_url, code, enabled]);

  // Radio takes over → pause the country song.
  useEffect(() => {
    if (radioPlaying) { audioRef.current?.pause(); setPlaying(false); }
  }, [radioPlaying]);

  const pause = () => { audioRef.current?.pause(); setPlaying(false); };
  const toggle = () => {
    if (playing) { pause(); trackEvent("song_pause", { country_code: code }); return; }
    audioRef.current?.play().then(() => { setPlaying(true); radioRef.current?.pause(); }).catch(() => {});
    trackEvent("song_play", { country_code: code });
  };

  return { audioRef, playing, progress, setProgress, toggle, pause };
}

/** Persisted UI preferences: classic/modern switch and music on/off. */
function useSongPrefs(code, modern, pause) {
  const [kind, setKind] = useState(() => (storage.get(KIND_KEY) === "modern" && modern ? "modern" : "classic"));
  const [enabled, setEnabled] = useState(() => storage.get(MUSIC_KEY) === "on"); // opt-in: the app starts silent

  const switchKind = (k) => {
    if (k === kind) return;
    storage.set(KIND_KEY, k);
    setKind(k);
    trackEvent("song_kind", { kind: k, country_code: code });
  };

  const toggleEnabled = () => {
    const next = !enabled;
    storage.set(MUSIC_KEY, next ? "on" : "off");
    setEnabled(next);
    if (!next) pause();
    trackEvent("music_toggle", { enabled: next });
  };

  return { kind, enabled, switchKind, toggleEnabled };
}

export default function SongPlayer({ song: classic, modern, code }) {
  const { t } = useLang();
  const pauseRef = useRef(() => {});
  const { kind, enabled, switchKind, toggleEnabled } = useSongPrefs(code, modern, () => pauseRef.current());
  const song = kind === "modern" && modern ? modern : classic;
  const { audioRef, playing, progress, setProgress, toggle, pause } = useSongAudio(song, code, enabled);
  pauseRef.current = pause;

  if (!song) return null;

  return (
    <div className="mx-6 mt-4 flex items-center gap-3 rounded-2xl border border-stoneLine bg-ink text-sand p-2.5 pe-3" data-testid="song-player">
      <ArtworkButton song={song} playing={playing} onClick={toggle} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-[0.15em] text-sand/60 truncate ${classic && modern ? "hidden sm:inline" : ""}`}>{t("song_label")}</span>
          {classic && modern && <KindSwitch kind={kind} onChange={switchKind} />}
        </div>
        <div className="text-sm font-semibold truncate" dir="auto" data-testid="song-title">{song.title}</div>
        <div className="text-xs text-sand/70 truncate" dir="auto" data-testid="song-artist">{song.artist}</div>
        <div className="mt-1.5 h-1 rounded-full bg-sand/15 overflow-hidden">
          <div className="h-full bg-ochre transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2"><ListenFull song={song} countryCode={code} compact /></div>
      </div>
      <MusicToggle enabled={enabled} onToggle={toggleEnabled} />
      <audio
        ref={audioRef}
        src={src(song.preview_url)}
        preload="auto"
        onTimeUpdate={(e) => setProgress((e.target.currentTime / (e.target.duration || 30)) * 100)}
        onEnded={() => { pause(); setProgress(100); }}
        data-testid="song-audio"
      />
    </div>
  );
}
